import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import type { Provider } from "@/types";
import { personName } from "@/lib/championships";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";

const schema = z.object({
  nombre: z.string().min(2, "Mínimo 2 caracteres"),
  descripcion: z.string().optional(),
  providerId: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export function ChampionshipCreatePage() {
  const navigate = useNavigate();
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get<{ data: Provider[] }>("/providers?limit=100")).data.data,
    enabled: isSuperAdmin,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const create = useMutation({
    mutationFn: (data: FormValues) =>
      api.post<{ id: number }>("/championships", {
        nombre: data.nombre,
        descripcion: data.descripcion || null,
        ...(isSuperAdmin ? { providerId: data.providerId ? Number(data.providerId) : null } : {}),
      }),
    onSuccess: ({ data }) => {
      toast.success("Campeonato creado");
      navigate(`/campeonatos/${data.id}`);
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo crear el campeonato"),
  });

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Campeonatos", to: "/campeonatos" }, { label: "Nuevo campeonato" }]} />

      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <Trophy className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Nuevo campeonato</h1>
          <p className="text-ink-400">Crea el campeonato; después podrás inscribir parejas y programar los partidos desde su ficha.</p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit((data) => create.mutate(data))} className="flex flex-col gap-4">
          <Input id="campeonato-nombre" label="Nombre del campeonato" error={errors.nombre?.message} {...register("nombre")} />

          {isSuperAdmin && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="campeonato-provider" className="text-sm font-medium text-ink-600">
                Organizador
              </label>
              <select
                id="campeonato-provider"
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                {...register("providerId")}
              >
                <option value="">Plataforma (sin proveedor concreto: se pueden usar todos los clubes)</option>
                {providers?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {personName(p)} ({p.clubs.length} club{p.clubs.length === 1 ? "" : "es"})
                  </option>
                ))}
              </select>
              <p className="text-xs text-ink-400">
                Con un proveedor, el campeonato solo puede usar sus clubes (pistas y jugadores) y solo ese proveedor puede darle
                permiso de gestión a sus usuarios de club.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="campeonato-descripcion" className="text-sm font-medium text-ink-600">
              Descripción (opcional)
            </label>
            <textarea
              id="campeonato-descripcion"
              rows={3}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              {...register("descripcion")}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" onClick={() => navigate("/campeonatos")}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending}>
              Crear campeonato
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
